/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * `/products?tab=tlds` "TLD & bảng giá" (reference screen 18): every zone we sell with its selling
 * prices in one price book (register / renew / transfer in / restore, engine-priced), whether a
 * new price is scheduled, and whether it is ready to sell. The picked row's profile (rules,
 * registration connection, cost) and its price versions sit on the right; how a selling price is
 * made sits under the list. "Thêm TLD" opens the setup wizard (`?action=add-tld` too).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type { MatrixCell, PriceBook, PriceMatrix, PriceRule } from '../../api/areas/commerce';
import type { RegistryStatus, Tld, TldClass } from '../../api/areas/registry';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { FactRows, QuickLinks, SplitPane, Stepper, Timeline, type TimelineItem } from '../../components/flow';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { Amount } from '../commerce/shared';
import { useRuleWords } from '../commerce/PriceMatrix';
import { channelOf, ChannelBadge } from '../integrations/shared';
import { EnumBadge, enumLabel, FilterSelect, Flag, Pager, REGISTRY_STATUS, RefreshButton, RegistryRef, RegistrySelect, ResetButton, useParamSetter, useRegistryCrumbs } from './shared';
import { connectionUrl, PublicationText, publisherOf, ReadinessBadge, tldUrl } from './readiness';
import { TldSetupButton } from './TldSetupWizard';

type TFn = (key: string, o?: Record<string, unknown>) => string;
const OPS = ['REGISTER', 'RENEW', 'TRANSFER', 'RESTORE'] as const;
const CLASSES: TldClass[] = ['VN', 'GTLD', 'NEW_GTLD', 'CCTLD', 'OTHER'];
/** The retail tier new customers get (`DEFAULT_TIER` in subsystems/registrar/facade/adapters/admin-api/src/admin/tld_setup.rs). */
const DEFAULT_TIER = 'STANDARD';

/** The book shown first: the default tier's book, what a new customer pays. */
function defaultBook(books: readonly PriceBook[]): PriceBook | undefined {
  const usable = books.filter((b) => b.status === 'ACTIVE');
  return usable.find((b) => b.applies_to === 'TIER' && b.tier === DEFAULT_TIER) ?? usable.find((b) => b.applies_to === 'TIER') ?? usable[0];
}

const isPriceRule = (r: PriceRule) => (OPS as readonly string[]).includes(r.operation) && !r.addon_code;

/** The earliest price of the zone (its own or the book-wide one) that starts later than now. */
function nextChange(book: PriceBook | undefined, tldId: string, now: string): string | null {
  const starts = (book?.rules ?? []).filter((r) => isPriceRule(r) && (r.tld_id === tldId || r.tld_id === null) && r.effective_from > now).map((r) => r.effective_from);
  return starts.length ? starts.sort()[0] ?? null : null;
}

/** One price without its currency (the column header carries it); a missing one says why on hover. */
function PriceCell({ cell, currency }: { cell: MatrixCell | undefined; currency: string }) {
  const { t } = useTranslation('registry');
  if (!cell) return <span className="text-muted-foreground">—</span>;
  if (cell.source === 'FREE' || cell.basis === 'FREE') return <span className="text-muted-foreground">{t('hub.free')}</span>;
  if (cell.source === 'NOT_OFFERED') return <span className="text-muted-foreground">{t('hub.notOffered')}</span>;
  if (cell.amount === null) {
    const why = cell.source === 'NO_COST' ? t('hub.noCost') : t('hub.noRule');
    return (
      <span className="text-xs text-warning" title={why}>
        {t('hub.missingShort')}
        <span className="sr-only">: {why}</span>
      </span>
    );
  }
  const full = formatMoney({ amount: cell.amount, currency });
  return (
    <span className="tabular whitespace-nowrap" title={full}>
      {full.replace(` ${currency}`, '')}
    </span>
  );
}

/** How a selling price is made, and the settings that change it (each opens its page). */
function PriceModel() {
  const { t } = useTranslation('registry');
  const parts = [
    { key: 'premium', to: null },
    { key: 'promotions', to: '/products?tab=promotions' },
    { key: 'fx', to: '/products?tab=pricing' },
    { key: 'tax', to: '/products?tab=tax' },
    { key: 'addons', to: '/products?tab=services' },
  ] as const;
  return (
    <Section title={t('hub.model.title')} description={t('hub.model.description')}>
      <Stepper
        label={t('hub.model.title')}
        steps={[
          { id: 'cost', label: t('hub.model.cost'), hint: t('hub.model.costHint') },
          { id: 'margin', label: t('hub.model.margin'), hint: t('hub.model.marginHint') },
          { id: 'sale', label: t('hub.model.sale'), hint: t('hub.model.saleHint'), state: 'current' },
          { id: 'quote', label: t('hub.model.quote'), hint: t('hub.model.quoteHint') },
        ]}
      />
      <ul className="mt-4 grid gap-x-6 gap-y-3 border-t pt-4 sm:grid-cols-2">
        {parts.map((p) => (
          <li key={p.key} className="min-w-0 text-[13px]">
            {p.to ? (
              <Link to={p.to} className="font-semibold text-primary hover:underline">
                {t(`hub.model.${p.key}`)}
              </Link>
            ) : (
              <span className="font-semibold">{t(`hub.model.${p.key}`)}</span>
            )}
            <span className="block text-xs text-muted-foreground">{t(`hub.model.${p.key}Hint`)}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** The picked zone: rules, registration connection, cost and readiness, with the ways in. */
function TldProfile({ tld, cells, currency }: { tld: Tld | undefined; cells: MatrixCell[] | undefined; currency: string }) {
  const { t } = useTranslation('registry');
  const tr = t as unknown as TFn;
  if (!tld) {
    return (
      <Section title={t('tlds.title')}>
        <p className="text-sm text-muted-foreground">{t('hub.profile.none')}</p>
      </Section>
    );
  }
  const channel = tld.readiness?.channel;
  const cost = cells?.find((c) => c.operation === 'REGISTER')?.registry_cost ?? null;
  return (
    <Section title={t('hub.profile.title', { zone: tld.zone })} description={t('hub.profile.description')}>
      <FactRows
        rows={[
          [
            t('hub.col.connection'),
            channel ? (
              <span key="conn" className="inline-grid justify-items-end gap-1">
                <Link to={connectionUrl(channel)} className="font-medium text-primary hover:underline">
                  {channel.name}
                </Link>
                <span className="flex flex-wrap justify-end gap-1">
                  <ChannelBadge channel={channelOf(channel.kind === 'UPSTREAM_REGISTRAR' ? 'UPSTREAM_REGISTRAR' : 'REGISTRY', channel.protocol === 'REST' ? 'REST' : 'EPP')} short />
                  <span className="text-xs text-muted-foreground">{tr(`readiness.source.${channel.source}`)}</span>
                </span>
              </span>
            ) : (
              '—'
            ),
          ],
          [t('tlds.registry'), <RegistryRef key="r" name={tld.registry_name} code={tld.registry_code} />],
          [t('tlds.class'), enumLabel('tldClass', tld.tld_class)],
          [t('tlds.dossier'), enumLabel('dossierType', tld.dossier_type)],
          [t('tlds.icann'), <Flag key="i" on={tld.icann_governed} yes={t('tlds.governed')} no={t('no')} />],
          [
            t('tlds.rdapWhois'),
            <PublicationText key="p" publisher={tld.readiness?.publisher ?? publisherOf(tld.rdap_enabled, tld.whois_enabled, undefined, tld.tld_class)} connection={channel ?? { name: tld.registry_name }} />,
          ],
          [t('hub.profile.costRegister'), cost !== null ? <Amount key="c" amount={cost} currency={currency} /> : '—'],
          [t('tlds.managed'), formatCount(tld.managed_domains)],
          [t('tlds.status'), <EnumBadge key="s" group="registryStatus" map={REGISTRY_STATUS} value={tld.status} />],
          [t('readiness.title'), <ReadinessBadge key="ready" summary={tld.readiness} to={tldUrl(tld.id)} />],
        ]}
      />
      <div className="mt-4 flex flex-wrap gap-2">
        <Button asChild size="sm">
          <Link to={tldUrl(tld.id)}>{t('hub.profile.open')}</Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to={tldUrl(tld.id, 'pricing')}>{t('hub.profile.prices')}</Link>
        </Button>
        <Button asChild size="sm" variant="outline">
          <Link to={tldUrl(tld.id, 'channels')}>{t('hub.profile.channel')}</Link>
        </Button>
      </div>
    </Section>
  );
}

/** Selling prices of the picked zone in the book over time: in force, scheduled, replaced. */
function PriceVersions({ tld, book }: { tld: Tld | undefined; book: PriceBook | undefined }) {
  const { t } = useTranslation('registry');
  const tr = t as unknown as TFn;
  const words = useRuleWords();
  if (!tld || !book) return null;
  const now = new Date().toISOString();
  const rules = book.rules
    .filter((r) => isPriceRule(r) && (r.tld_id === tld.id || r.tld_id === null))
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))
    .slice(0, 8);
  const items: TimelineItem[] = rules.map((r) => {
    const state = r.effective_from > now ? 'scheduled' : r.effective_until !== null && r.effective_until <= now ? 'ended' : 'active';
    const op = tr(`setup.op.${r.operation}`);
    return {
      id: r.id,
      title: r.tld_id ? t('hub.versions.op', { op, version: r.version }) : t('hub.versions.shared', { op, version: r.version }),
      time: r.effective_until ? t('hub.versions.windowTo', { from: formatDate(r.effective_from), to: formatDate(r.effective_until) }) : t('hub.versions.window', { from: formatDate(r.effective_from) }),
      meta: words(r, book.currency),
      body: <Badge tone={state === 'active' ? 'ok' : state === 'scheduled' ? 'pending' : 'neutral'}>{t(`hub.versions.${state}`)}</Badge>,
      tone: state === 'active' ? 'ok' : state === 'scheduled' ? 'pending' : 'neutral',
    };
  });
  return (
    <Section title={t('hub.versions.title', { book: book.code })} description={t('hub.versions.description', { zone: tld.zone })}>
      <Timeline items={items} empty={t('hub.versions.empty', { zone: tld.zone })} />
      <p className="mt-4 text-xs text-muted-foreground">{t('hub.versions.approval')}</p>
    </Section>
  );
}

export function TldsPage() {
  const { t } = useTranslation('registry');
  useDocumentTitle(t('tlds.title'));
  const crumbs = useRegistryCrumbs();
  const [params, set, reset] = useParamSetter();
  const registry = params.get('registry') ?? '';
  const status = (params.get('status') ?? '') as RegistryStatus | '';
  const after = params.get('after') ?? '';
  const cls = (params.get('class') ?? '') as TldClass | '';
  const q = params.get('q') ?? '';
  const [draft, setDraft] = useState(q);
  // The picked row is local: picking must not reset the page cursor the way a filter does.
  const [pickedId, setPickedId] = useState('');
  // Ctrl-K "Thiết lập TLD mới" lands here with `action=add-tld`: the wizard opens once.
  const [openSetup] = useState(() => params.get('action') === 'add-tld');
  useEffect(() => {
    if (params.get('action') === 'add-tld') set('action', '');
    // oxlint-disable-next-line react/exhaustive-deps -- once, on arrival
  }, []);
  const result = useApi(
    (signal) => api.listTlds({ registry: registry || undefined, status: status || undefined, after: after || undefined, limit: 200 }, { signal }),
    [registry, status, after],
  );
  // Every book with its whole rule history: the versions panel and the scheduled prices.
  const pricing = useApi((signal) => api.getPricing({ history: true }, { signal }), []);
  const books = pricing.status === 'success' ? pricing.data.price_books.filter((b) => b.status === 'ACTIVE') : [];
  const bookId = params.get('book') ?? '';
  const book = books.find((b) => b.id === bookId) ?? defaultBook(books);
  const matrix = useApi((signal) => (book ? api.getPriceBookMatrix(book.id, {}, { signal }) : Promise.resolve(null as PriceMatrix | null)), [book?.id]);
  const cellsOf = useMemo(() => {
    const m = new Map<string, MatrixCell[]>();
    if (matrix.status === 'success' && matrix.data) for (const r of matrix.data.rows) m.set(r.tld_id, r.cells);
    return m;
  }, [matrix]);
  const currency = matrix.status === 'success' && matrix.data ? matrix.data.currency : (book?.currency ?? '');
  const now = new Date().toISOString();

  const all = result.status === 'success' ? result.data.items : [];
  const needle = q.trim().replace(/^\./, '').toLowerCase();
  const rows = all.filter(
    (z) =>
      (!cls || z.tld_class === cls) &&
      (!needle || z.zone.includes(needle) || z.registry_name.toLowerCase().includes(needle) || (z.readiness?.channel.name ?? '').toLowerCase().includes(needle)),
  );
  const picked = rows.find((z) => z.id === pickedId) ?? rows[0];
  const cell = (z: Tld, op: (typeof OPS)[number]) => cellsOf.get(z.id)?.find((c) => c.operation === op);

  const columns: Column<Tld>[] = [
    {
      key: 'zone',
      header: t('hub.col.tld'),
      cell: (z) => (
        <span className="grid max-w-44 gap-0.5">
          <span className="flex flex-wrap items-center gap-1.5">
            <Link to={tldUrl(z.id)} className="font-mono text-[15px] font-semibold text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
              .{z.zone}
            </Link>
            <Badge tone={z.tld_class === 'VN' ? 'info' : 'neutral'} icon={false}>
              {enumLabel('tldClass', z.tld_class)}
            </Badge>
          </span>
          <span className="text-xs text-muted-foreground" title={t('hub.col.connection')}>
            {z.readiness?.channel.name ?? z.registry_name}
          </span>
        </span>
      ),
    },
    ...OPS.map(
      (op): Column<Tld> => ({
        key: op,
        header: t(`hub.col.${op === 'REGISTER' ? 'register' : op === 'RENEW' ? 'renew' : op === 'TRANSFER' ? 'transfer' : 'restore'}`),
        numeric: true,
        cell: (z) => <PriceCell cell={cell(z, op)} currency={currency} />,
      }),
    ),
    {
      key: 'state',
      header: t('hub.col.state'),
      cell: (z) => {
        const next = nextChange(book, z.id, now);
        return (
          <span className="grid justify-items-start gap-1">
            <ReadinessBadge summary={z.readiness} to={tldUrl(z.id)} />
            {next ? <span className="text-xs text-primary">{t('hub.effectiveFrom', { date: formatDate(next) })}</span> : cell(z, 'REGISTER')?.amount ? <span className="text-xs text-muted-foreground">{t('hub.effectiveNow')}</span> : null}
          </span>
        );
      },
    },
  ];

  const list = (
    <Section flush>
      <div className="px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            set('q', draft.trim());
          }}
        >
          <FilterBar actions={<ResetButton onClick={() => (setDraft(''), reset())} disabled={!registry && !status && !cls && !q} />}>
            <Field label={t('hub.search')} className="w-full sm:w-56">
              <Input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => set('q', draft.trim())} placeholder={t('hub.searchPlaceholder')} />
            </Field>
            <FilterSelect label={t('hub.group')} value={cls} onChange={(v) => set('class', v)} options={CLASSES.map((c) => ({ value: c, label: enumLabel('tldClass', c) }))} />
            <RegistrySelect value={registry} onChange={(v) => set('registry', v)} />
            <FilterSelect label={t('filter.status')} value={status} onChange={(v) => set('status', v)} options={Object.keys(REGISTRY_STATUS).map((s) => ({ value: s, label: enumLabel('registryStatus', s) }))} />
          </FilterBar>
        </form>
      </div>
      {result.status === 'loading' ? (
        <div className="px-5 pb-5">
          <Loading rows={4} />
        </div>
      ) : null}
      {result.status === 'error' ? (
        <div className="px-5 pb-5">
          <DataError error={result.error} onRetry={result.reload} title={t('tlds.loadError')} />
        </div>
      ) : null}
      {result.status === 'success' ? (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(z) => z.id}
            onRowClick={(z) => setPickedId(z.id)}
            rowClassName={(z) => cn(z.id === picked?.id && 'bg-accent/50 hover:bg-accent/60')}
            empty={<EmptyState title={t('tlds.empty')} />}
          />
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">{t('hub.shown', { shown: rows.length, total: all.length })}</p>
          <Pager next={result.data.next_after} onNext={(c) => set('after', c)} first={!after} onFirst={() => set('after', '')} />
        </>
      ) : null}
    </Section>
  );

  return (
    <>
      <PageHeader
        title={t('tlds.title')}
        crumbs={crumbs}
        subtitle={t('tlds.subtitle')}
        actions={
          <>
            <TldSetupButton onChanged={result.reload} defaultOpen={openSetup} />
            <RefreshButton
              onClick={() => {
                result.reload();
                pricing.reload();
                matrix.reload();
              }}
            />
          </>
        }
      >
        <QuickLinks
          label={t('hub.links')}
          links={[
            { label: t('hub.link.channels'), to: '/settings?tab=domains&sub=channels' },
          ]}
        />
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        {book ? (
          <p className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
            <Badge tone="info" icon={false} className="font-mono">
              {book.code}
            </Badge>
            {book.applies_to === 'TIER' ? t('hub.bookNote', { tier: book.tier ?? '', currency: book.currency }) : t('hub.bookNoteOther', { currency: book.currency })}
          </p>
        ) : pricing.status === 'success' ? (
          <p className="text-[13px] text-muted-foreground">{t('hub.noBook')}</p>
        ) : (
          <span />
        )}
        {books.length > 1 ? (
          <FilterSelect label={t('hub.book')} value={book?.id ?? ''} onChange={(v) => set('book', v)} options={books.map((b) => ({ value: b.id, label: b.code }))} className="min-w-48" />
        ) : null}
      </div>
      {pricing.status === 'error' ? <Callout tone="neutral" className="mb-4">{t('hub.pricesHidden')}</Callout> : null}
      <SplitPane
        className="xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_400px]"
        list={
          <div className="space-y-5">
            {list}
            <PriceModel />
          </div>
        }
        detail={
          <div className="space-y-5">
            <TldProfile tld={picked} cells={picked ? cellsOf.get(picked.id) : undefined} currency={currency} />
            <PriceVersions tld={picked} book={book} />
          </div>
        }
      />
    </>
  );
}
